/**
 * The `/saved` screen's UI state (§12.4, SPEC `saved-posts`).
 *
 * The `useFeedUiState` contract under a different key: `saved:main` is loaded **before the first
 * #66 fetch** — the query stays disabled until `ready` flips, so a persisted selection is never
 * briefly ignored and page one is never fetched twice — defaulted on `404`, and written back
 * debounced (500 ms), flushed on unmount.
 *
 * `q` is deliberately absent from the stored blob: search on this screen is session-only.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { SAVED_UI_STATE_KEY, UI_STATE_MAX_BYTES } from '@lw/shared';
import { getUiState, putUiState } from '../../api/uiState';
import { QK } from '../../api/queryKeys';
import { useDebouncedCallback, useLatest } from '../../lib/hooks';
import {
  DEFAULT_SAVED_STATE,
  sanitizeSavedState,
  toStoredSavedState,
  type SavedState,
} from './savedState';

const WRITE_DEBOUNCE_MS = 500;

export type SavedUiStateHandle = {
  /** `false` until the stored value (or its 404) has arrived — gate the #66 query on this. */
  ready: boolean;
  state: SavedState;
  update: (patch: Partial<SavedState>) => void;
  /** Loading the state failed; the screen offers a retry rather than guessing. */
  error: unknown;
  retry: () => void;
};

export function useSavedUiState(): SavedUiStateHandle {
  const query = useQuery({
    queryKey: QK.uiState(SAVED_UI_STATE_KEY),
    queryFn: ({ signal }) => getUiState<unknown>(SAVED_UI_STATE_KEY, signal),
    staleTime: Number.POSITIVE_INFINITY,
    // A cached value from a previous visit is authoritative; refetching would race the writer.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const [state, setState] = useState<SavedState | null>(null);
  const seeded = useRef(false);
  const fellBack = useRef(false);

  // Seed once: from whatever the server had, from the defaults on 404, and — so a broken ui-state
  // endpoint can never brick the screen — from the defaults on failure too.
  useEffect(() => {
    if (query.isSuccess && !seeded.current) {
      seeded.current = true;
      fellBack.current = false;
      setState(sanitizeSavedState(query.data));
      return;
    }
    if (query.isError && !seeded.current && !fellBack.current) {
      fellBack.current = true;
      setState(sanitizeSavedState(null));
    }
  }, [query.isSuccess, query.isError, query.data]);

  const persist = useDebouncedCallback((next: SavedState) => {
    const payload = toStoredSavedState(next);
    if (JSON.stringify(payload).length > UI_STATE_MAX_BYTES) return;
    void putUiState(SAVED_UI_STATE_KEY, payload).catch(() => {
      // Persistence is best-effort: the screen keeps working with in-memory state.
    });
  }, WRITE_DEBOUNCE_MS);

  // Driven from a ref rather than from inside the state updater: React skips updaters for an
  // unmounted component, and the last change before navigating away must still reach the server.
  const stateRef = useLatest(state);

  const update = useCallback(
    (patch: Partial<SavedState>) => {
      const next: SavedState = { ...(stateRef.current ?? DEFAULT_SAVED_STATE), ...patch };
      setState(next);
      persist(next);
    },
    [persist, stateRef],
  );

  return {
    ready: state !== null,
    state: state ?? DEFAULT_SAVED_STATE,
    update,
    error: query.isError ? query.error : null,
    retry: () => void query.refetch(),
  };
}
